import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import toastr from 'toastr';
import {
    Button,
    CardBody,
    Col,
    Modal,
    ModalHeader,
    Popover,
    PopoverBody,
    Row,
    Spinner,
    UncontrolledTooltip
} from "reactstrap";
import CardItem from "components/PurpleTheme/CardItem";
import Loader from "../../components/Loader/Loader";
import delpoyAlertsLottie from '../../assets/images/deploy_alerts_lottie.json';
import {useDispatch, useSelector} from "react-redux";
import {
    getDynatraceAllUserActions,
    postCreateKeyUserAction,
} from '../../store/actions';
import {getDynatraceLogo, getDynatraceName} from "../../helpers/component_helpers";
import UserActionResourceActions from "./UserActionResourceActions";
import {showNotification} from "components/Common/notification";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const DynatraceUserActions = (props) => {
    document.title = "Dynatrace User Actions | Temperstack";
    const params = useParams();
    const dispatch = useDispatch();
    const {dynatrace_all_user_actions} = useSelector(state => ({
        dynatrace_all_user_actions: state.Dynatrace.dynatrace_all_user_actions || [],
    }));
    const [hoveredResource, setHoveredResource] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [selectedResourceId, setSelectedResourceId] = useState(null);
    const [isLoading2, setIsLoading2] = useState(false);
    const [selectedPage, setSelectedPage] = useState(1);
    const itemsPerPage = 40;
    const [filteredResources, setFilteredResources] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isDateOpen, setIsDateOpen] = useState(false);
    const toggle = () => setIsOpen(!isOpen);
    const toggleDates = () => setIsDateOpen(!isDateOpen);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const total_resources = filteredResources?.count || 0;
    const resourceCount = filteredResources && filteredResources.monitored_resources ? filteredResources.monitored_resources.length : 0;

    useEffect(() => {
        setFilteredResources(dynatrace_all_user_actions);
        setIsLoading(false);
    }, [dynatrace_all_user_actions]);
    const searchParams = new URLSearchParams(location.search);
    const searchQuery = searchParams.get("parent_resource_id");
    const parent_resource_id = searchQuery ? searchQuery : null;
    const searchQueryName = searchParams.get("parent_resource_name");
    const parent_resource_name = searchQueryName ? searchQueryName : null;

    let pageFilters = {}
    useEffect(() => {
        pageFilters['parent_resource_id'] = parent_resource_id
        dispatch(getDynatraceAllUserActions(pageFilters));
    }, [getDynatraceAllUserActions]);

    const startLoading2 = () => {
        setIsLoading2(true);

        setTimeout(() => {
            setIsLoading2(false);
        }, 5000);
    };

    const headers = [
        {label: "Resource Name", width: 2, marginBottom: 2, textCenter: true},
        {label: "Type", width: 2, marginBottom: 2, textCenter: true},
        {label: "Parent Cloud ID", width: 2, marginBottom: 2, textCenter: true},
        {label: "Parent Resource Type", width: 2, marginBottom: 2, textCenter: true},
        {label: "Created At", width: 2, marginBottom: 2, textCenter: true},
        {label: "Actions", width: 2, marginBottom: 2, textCenter: true},
    ];

    const handleDeployAction = (resourceId) => {
        setSelectedResourceId(resourceId);
        setShowConfirmationModal(true);
    }

    const confirmDeployAction = () => {
        setShowConfirmationModal(false);
        const payload = {
            'action_monitored_resource_id': selectedResourceId,
            'parent_resource_monitored_resource_id': parent_resource_id
        };
        dispatch(postCreateKeyUserAction(payload));
    }

    const handleMouseEnter = (resourceId) => {
        setHoveredResource(resourceId);
    };

    const handleMouseLeave = () => {
        setHoveredResource(null);
    };

    const copyToClipboard = (textToCopy) => {

        if (navigator.clipboard) {
            navigator.clipboard.writeText(textToCopy)
                .then(() => toastr.success("Copied!"))
                .catch(err => console.error('Could not copy text: ', err));
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = textToCopy;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                toastr.success("Copied!");
            } catch (err) {
                console.error('Could not copy text: ', err);
            }
            document.body.removeChild(textarea);
        }
    };

    const handlePageClick = (pageNumber) => {
        setIsLoading(true);
        if (pageNumber < 1) {
            setIsLoading(false);
            return;
        }
        if (selectedPage < pageNumber && filteredResources.length < itemsPerPage) {
            setIsLoading(false);
            return;
        }

        setSelectedPage(pageNumber);

        const formattedStartDate = startDate ? formatDate(startDate) : '';
        const formattedEndDate = endDate ? formatDate(endDate) : '';

        pageFilters['parent_resource_id'] = parent_resource_id
        pageFilters['page'] = pageNumber;
        pageFilters['limit'] = itemsPerPage;
        pageFilters['search_item'] = search;
        pageFilters['start_date'] = formattedStartDate;
        pageFilters['end_date'] = formattedEndDate;
        dispatch(getDynatraceAllUserActions(pageFilters));
        setSelectedPage(pageNumber);
    };

    function formatDate(dates) {
        const date = new Date(dates);
        const utcDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
        return utcDate.toUTCString();
    }

    function formatCreatedAtDate(dateString) {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    const handleStartDateChange = (event) => {
        const dateValue = event.target.value;
        setStartDate(dateValue ? new Date(dateValue) : null);
    };

    const handleEndDateChange = (event) => {
        const dateValue = event.target.value;
        const newEndDate = dateValue ? new Date(dateValue) : null;
        if (startDate && newEndDate && newEndDate < startDate) {
            showNotification("error", "End date cannot be before the start date")
            return;
        }
        setEndDate(newEndDate);
    };

    const handlePredefinedDateChange = (days) => {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(today.getDate() - days);

        setStartDate(startDate);
        setEndDate(today);
    };

    return (
        <React.Fragment>
            <Col lg="8">
                <Breadcrumbs
                    title={"Dynatrace User Actions"}
                    breadcrumbItem={`User Actions of ${parent_resource_name ? ` OF ${parent_resource_name}` : ''}`}
                />
            </Col>
            <Col lg="4">
            </Col>
            <div className="table-responsive react-table">
                <UserActionResourceActions
                    selectedPage={selectedPage}
                    total_resources={total_resources}
                    itemsPerPage={itemsPerPage}
                    handlePageClick={handlePageClick}
                    isOpen={isOpen}
                    toggle={toggle}
                    setSearch={setSearch}
                    startDate={startDate}
                    endDate={endDate}
                    handleStartDateChange={handleStartDateChange}
                    handleEndDateChange={handleEndDateChange}
                    toggleDates={toggleDates}
                    isDateOpen={isDateOpen}
                    handlePredefinedDateChange={handlePredefinedDateChange}
                />
            </div>
            <div className="header">
                <div className="resources-header-card">
                    <Row className="align-items-center padding-t-10">
                        {headers.map((header, index) => (
                            <Col md={header.width} key={index}>
                                <div className={`mb-${header.marginBottom} ${header.textCenter && "text-center"}`}>
                                    {header.label}
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>
            <div>
                <div className="scrollable-container">
                    {isLoading ? (
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <Spinner
                                color="primary"
                                style={{
                                    height: '3rem',
                                    width: '3rem'
                                }}
                            >
                                Loading...
                            </Spinner>
                        </div>
                    ) : filteredResources.length === 0 ? (
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <h2 className="resource-name-truncate">
                                {isLoading ? "Loading resources..." : "No matching resources found."}
                            </h2>
                        </div>
                    ) : (
                        filteredResources.monitored_resources.map((resource, index) => (
                            <CardItem key={index}>
                                <CardBody className="pt-0 pl-2 pr-2 pb-1">
                                    <Row className="align-items-center">
                                        <Col md="2" className="padding-t-15 align-items-center justify-content-center">
                                            <div style={{display: 'flex', alignItems: 'center'}}>
                                                <div className="resource-logo-display justify-content-center">
                                                    <img
                                                        src={getDynatraceLogo(resource.monitored_resource.master_resource_name, true)}
                                                        className="resource-logo"
                                                        alt="Resource Logo"
                                                    />
                                                </div>
                                                <div style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    justifyContent: 'center'
                                                }}
                                                     onMouseEnter={() => handleMouseEnter(resource.monitored_resource.id)}
                                                     onMouseLeave={handleMouseLeave}>
                                                    <h6 className="resource-name-truncate" id={`resourceName-${index}`}>
                                                        {resource.monitored_resource.name}
                                                    </h6>
                                                </div>
                                                {hoveredResource === resource.monitored_resource.id && (
                                                    <Popover placement="top" isOpen={true}
                                                             target={`resourceName-${index}`}>
                                                        <PopoverBody>{resource.monitored_resource.name}</PopoverBody>
                                                    </Popover>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md="2" className="padding-t-15 align-items-start">
                                            <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                <div className="mb-2 text-center">
                                                    {getDynatraceName(resource.monitored_resource.master_resource_name)}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="2" className="padding-t-15 align-items-start">
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignContent: 'center',
                                                marginBottom: '5px'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    fontSize: '1em',
                                                    clear: 'both'
                                                }}>
                                                    <div style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {resource.parent_resource.dynatrace_reference_id}
                                                    </div>
                                                    <div className="copy-cloud-id" style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        marginLeft: '2px'
                                                    }}>
                                                        <i className="bx bx-copy"
                                                           style={{cursor: 'pointer', width: '15px', height: '15px'}}
                                                           onClick={() => {
                                                               copyToClipboard(resource.parent_resource.dynatrace_reference_id)
                                                           }} id={`cloudId-${index}`}></i>
                                                        <UncontrolledTooltip placement="top"
                                                                             target={`cloudId-${index}`}>
                                                            Copy Cloud ID
                                                        </UncontrolledTooltip>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="2" className="padding-t-15">
                                            <div style={{display: 'flex', justifyContent: 'center', gap: '10px'}}>
                                                <div className="mb-2 text-center">
                                                    {getDynatraceName(resource.parent_resource.master_resource_name)}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="2" className="padding-t-15">
                                            <div
                                                style={{display: 'flex', justifyContent: 'center', marginLeft: '20px'}}>
                                                <div className="text-center mb-2">
                                                    {formatCreatedAtDate(resource.monitored_resource.created_at)}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md="2" className="padding-t-15">
                                            <div className='text-success' style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <button className="btn btn-primary waves-effect waves-light"
                                                        onClick={() => {
                                                            handleDeployAction(resource.monitored_resource.id)
                                                        }}> Mark as Key User Action
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </CardItem>
                        ))
                    )}
                </div>
            </div>
            <Loader
                isOpen={isLoading2}
                lottieJson={delpoyAlertsLottie}
                messageHeader="Marking As Key transactions"
                message="That was easy! Hang tight while we mark your transaction as key transaction."
            />
            <Modal isOpen={showConfirmationModal} role="dialog" autoFocus={true} keyboard={false} centered
                   backdrop="static" data-toggle="modal" toggle={() => setShowConfirmationModal(false)}>
                <div style={{backgroundColor: "#382b42"}}>
                    <div style={{backgroundColor: "#382b42"}}>
                        <ModalHeader className="border-bottom-2 justify-content-center">
                            <span className="modal-header-resource">Confirm Deployment</span>
                        </ModalHeader>
                    </div>
                    <CardItem>
                        <Row className="full-width">
                            <Col lg="12" className="mt-4">
                                <div className="text-center mb-4">
                                    <div className="row justify-content-center">
                                        <div className="col-xl-10">
                                            <h4>Are you sure you want to mark this Action as a key
                                                User Action?</h4>
                                            <Row className="full-width">
                                                <Col lg="6" className="mt-4">
                                                    <Button color="secondary"
                                                            onClick={() => setShowConfirmationModal(false)}
                                                            className="btn w-md">
                                                        Cancel
                                                    </Button>
                                                </Col>
                                                <Col lg="6" className="mt-4">
                                                    <Button color="primary" onClick={() => {
                                                        confirmDeployAction();
                                                        startLoading2();
                                                    }} className="btn w-md">
                                                        Confirm
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </CardItem>
                </div>
            </Modal>
            <h6 style={{textAlign: 'right', marginTop: '5px'}}>Showing {resourceCount} of {total_resources} Results</h6>
        </React.Fragment>
    );
};

DynatraceUserActions.propTypes = {};

export default DynatraceUserActions;
